import { queryOptions } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { apiCredentialKeys, parseApiCredential, type ApiCredential } from './entity';

const STALE_TIME = 60_000;

/** @public Fetch every tracking API credential for the account (all environments). */
export async function getApiCredentials(accountId: number): Promise<ApiCredential[]> {
  const data = await hydraUiApi.apiCredentials.list({
    params: { path: { account_id: String(accountId) } },
  });
  return (data.api_credentials ?? []).map(parseApiCredential);
}

export function getApiCredentialsQueryOptions(accountId: number) {
  return queryOptions({
    queryKey: apiCredentialKeys.lists(accountId),
    queryFn: () => getApiCredentials(accountId),
    staleTime: STALE_TIME,
  });
}

/**
 * @public Fetch only the tracking API credentials for one environment. The
 * account list endpoint filters on the `environment_id` query param, so this
 * returns a per-workspace count (the account-wide list would over-count for
 * multi-workspace accounts).
 */
export async function getApiCredentialsForEnvironment(
  accountId: number,
  environmentId: number,
): Promise<ApiCredential[]> {
  const data = await hydraUiApi.apiCredentials.list({
    params: { path: { account_id: String(accountId) }, query: { environment_id: environmentId } },
  });
  return (data.api_credentials ?? []).map(parseApiCredential);
}

/**
 * Its own cache slot under the account's `list` prefix, keyed by environment.
 * `staleTime: 0` (read non-cached, refetch on mount) — the underlying resource
 * is loaded fresh per route entry and edited by the credential mutations.
 */
export function getApiCredentialsForEnvironmentQueryOptions(accountId: number, environmentId: number) {
  return queryOptions({
    queryKey: [...apiCredentialKeys.lists(accountId), 'environment', environmentId] as const,
    queryFn: () => getApiCredentialsForEnvironment(accountId, environmentId),
    staleTime: 0,
  });
}
